import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface ContactSectionProps {
  setActiveSection: (section: string) => void;
}

const ContactSection = ({ setActiveSection }: ContactSectionProps) => {
  const { ref, inView } = useInView({ 
    threshold: 0.2,
    triggerOnce: false 
  });

  useEffect(() => {
    if (inView) {
      setActiveSection('contact');
    }
  }, [inView, setActiveSection]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    }, 1500);
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-16 sm:py-20 lg:py-32 px-4 sm:px-6 lg:px-12 bg-black"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-10 sm:mb-14 lg:mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-[3px] text-brand-aqua mb-3 font-semibold">
            GET IN TOUCH
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight px-4">
            Start Your Project
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16">
          {/* Contact Form - Full width on mobile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1"
          >
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              {/* Full-width inputs */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-brand-aqua/50 transition-colors text-base"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-brand-aqua/50 transition-colors text-base"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-brand-aqua/50 transition-colors text-base"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-white/80 mb-2">
                  Service Interest
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-aqua/50 transition-colors cursor-pointer text-base"
                >
                  <option value="" className="bg-black">Select a service</option>
                  <option value="ai-image" className="bg-black">AI Image Generation</option>
                  <option value="ai-video" className="bg-black">AI Video Generation</option>
                  <option value="hybrid" className="bg-black">Hybrid AI Content</option>
                  <option value="ugc" className="bg-black">UGC Content</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white/80 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-brand-aqua/50 transition-colors resize-none text-base"
                  placeholder="Tell us about your project (max 500 characters)"
                />
                <p className="text-xs text-white/50 mt-2">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full px-8 py-4 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-xl text-white font-semibold text-base hover:shadow-2xl hover:shadow-brand-pink/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 text-sm text-center"
                >
                  Message sent successfully! We'll get back to you soon.
                </motion.div>
              )}
            </form>
          </motion.div>

          {/* Contact Info - Stacked on mobile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-1 lg:order-2 space-y-6 sm:space-y-8"
          >
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">
                Let's Create Together
              </h3>
              <p className="text-base sm:text-lg text-white/70 leading-relaxed">
                Ready to transform your vision into reality? Get in touch with our team and let's discuss how we can help bring your project to life.
              </p>
            </div>

            {/* Contact Cards - Stacked */}
            <div className="space-y-4 sm:space-y-5">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-brand-pink/20 to-brand-aqua/20 rounded-xl flex-shrink-0">
                    <i className="ri-mail-line text-xl text-brand-aqua"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">Email</h4>
                    <a href="mailto:hello@blackvelvetstudio.com" className="text-sm text-white/70 hover:text-brand-aqua transition-colors">
                      hello@blackvelvetstudio.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-brand-pink/20 to-brand-aqua/20 rounded-xl flex-shrink-0">
                    <i className="ri-phone-line text-xl text-brand-aqua"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">Phone</h4>
                    <a href="tel:+15551234567" className="text-sm text-white/70 hover:text-brand-aqua transition-colors">
                      +1 (555) 123-4567
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-brand-pink/20 to-brand-aqua/20 rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-brand-aqua"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">Location</h4>
                    <p className="text-sm text-white/70">
                      Los Angeles, CA
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links - Touch-friendly */}
            <div>
              <h4 className="text-base font-semibold text-white mb-4">Follow Us</h4>
              <div className="flex gap-3">
                {['instagram', 'twitter', 'linkedin', 'youtube'].map((social) => (
                  <a
                    key={social}
                    href={`https://${social}.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center bg-white/5 hover:bg-gradient-to-br hover:from-brand-pink hover:to-brand-aqua rounded-xl transition-all cursor-pointer border border-white/10"
                  >
                    <i className={`ri-${social}-line text-xl text-white`}></i>
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
